import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "react-toastify";
import { AlertTriangle, Eye, EyeOff, Trash2, Shield, Filter } from "lucide-react";
import { API } from "./adminConfig";

const ModerationTab = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilter, setSelectedFilter] = useState('all'); // all, flagged, hidden
  const [searchQuery, setSearchQuery] = useState('');
  const [showWarnModal, setShowWarnModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [warningMessage, setWarningMessage] = useState('Please follow community guidelines');

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/chat/messages?limit=100`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('Error loading messages');
    } finally {
      setLoading(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!window.confirm('Are you sure you want to permanently delete this message?')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/chat/messages/${messageId}`);
      toast.success('Message deleted successfully');
      fetchMessages(); // Refresh the list
    } catch (error) {
      console.error('Error deleting message:', error);
      toast.error('Error deleting message');
    }
  };

  const hideMessage = async (messageId) => {
    try {
      await axios.patch(`${API}/admin/chat/messages/${messageId}/hide`);
      toast.success('Message hidden successfully');
      fetchMessages(); // Refresh the list
    } catch (error) {
      console.error('Error hiding message:', error);
      toast.error('Error hiding message');
    }
  };

  const unhideMessage = async (messageId) => {
    try {
      await axios.patch(`${API}/admin/chat/messages/${messageId}/unhide`);
      toast.success('Message unhidden successfully');
      fetchMessages(); // Refresh the list
    } catch (error) {
      console.error('Error unhiding message:', error);
      toast.error('Error unhiding message');
    }
  };

  const warnUser = async () => {
    try {
      await axios.patch(`${API}/admin/users/${selectedUser.user_id}/warn?warning_message=${encodeURIComponent(warningMessage)}`);
      toast.success(`Warning sent to ${selectedUser.user_name}`);
      setShowWarnModal(false);
      setSelectedUser(null);
      setWarningMessage('Please follow community guidelines');
    } catch (error) {
      console.error('Error warning user:', error);
      toast.error('Error sending warning');
    }
  };

  const filteredMessages = messages.filter(message => {
    // Apply filter
    if (selectedFilter === 'flagged' && !message.flagged) return false;
    if (selectedFilter === 'hidden' && !message.hidden) return false;
    
    // Apply search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        message.content.toLowerCase().includes(query) ||
        message.user_name.toLowerCase().includes(query) ||
        message.user_email.toLowerCase().includes(query)
      );
    }
    
    return true;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-burgundy mx-auto"></div>
        <span className="mt-2 text-burgundy">Loading messages...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-3xl font-bold text-burgundy font-serif">Community Moderation</h2>
        <div className="flex gap-3">
          <Button onClick={fetchMessages} className="bg-burgundy hover:bg-burgundy/80 text-cream">
            <Search className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters and Search */}
      <Card className="bg-white border-burgundy/20">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search messages, users, or emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="border-burgundy/30 focus:border-gold"
              />
            </div>
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="px-3 py-2 border border-burgundy/30 rounded-md focus:border-gold"
            >
              <option value="all">All Messages</option>
              <option value="flagged">Flagged Only</option>
              <option value="hidden">Hidden Only</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Messages List */}
      <div className="space-y-4">
        {filteredMessages.length === 0 ? (
          <Card className="bg-white border-burgundy/20">
            <CardContent className="text-center py-8">
              <MessageCircle className="h-16 w-16 mx-auto mb-4 opacity-30 text-burgundy" />
              <p className="text-burgundy/60">No messages found matching your criteria.</p>
            </CardContent>
          </Card>
        ) : (
          filteredMessages.map((message) => (
            <Card key={message.message_id} className={`bg-white border-burgundy/20 ${message.hidden ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-burgundy">{message.user_name}</span>
                      <span className="text-sm text-burgundy/60">({message.user_email})</span>
                      {message.flagged && <AlertTriangle className="h-4 w-4 text-orange-500" />}
                      {message.hidden && <Eye className="h-4 w-4 text-gray-500" />}
                    </div>
                    <div className="text-xs text-burgundy/50 mb-2">
                      {formatDate(message.created_at)} • Room: {message.room_id}
                    </div>
                    <div className="text-gray-800 bg-gray-50 p-3 rounded-md">
                      {message.content}
                    </div>
                  </div>
                </div>
                
                <div className="flex gap-2 mt-3">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedUser({
                        user_id: message.user_id,
                        user_name: message.user_name,
                        user_email: message.user_email
                      });
                      setShowWarnModal(true);
                    }}
                    className="text-orange-600 border-orange-600 hover:bg-orange-50"
                  >
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Warn User
                  </Button>
                  
                  {message.hidden ? (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => unhideMessage(message.message_id)}
                      className="text-green-600 border-green-600 hover:bg-green-50"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Unhide
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => hideMessage(message.message_id)}
                      className="text-gray-600 border-gray-600 hover:bg-gray-50"
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Hide
                    </Button>
                  )}
                  
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => deleteMessage(message.message_id)}
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Warning Modal */}
      {showWarnModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
          <Card className="w-full max-w-md bg-white border-burgundy/20">
            <CardHeader>
              <CardTitle className="font-serif text-burgundy">Warn User</CardTitle>
              <CardDescription>
                Send a warning to {selectedUser.user_name} ({selectedUser.user_email})
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-2">
                    Warning Message
                  </label>
                  <textarea
                    value={warningMessage}
                    onChange={(e) => setWarningMessage(e.target.value)}
                    className="w-full p-3 border border-burgundy/30 rounded-md focus:border-gold"
                    rows={3}
                    placeholder="Enter warning message..."
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowWarnModal(false);
                      setSelectedUser(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={warnUser}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Send Warning
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Chat Rooms Management Tab Component

export default ModerationTab;
